// AvailabilityPage.jsx - Updated to use ReusableTable
import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { Helmet } from "react-helmet-async";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../utils/axios";
import { useSpace } from "../../../contexts/SpaceContext";
import { TeamAvailability } from "../TeamAvailability";

import {
  Box,
  Grid,
  Divider as MuiDivider,
  Breadcrumbs as MuiBreadcrumbs,
  Paper as MuiPaper,
  Snackbar,
  Alert,
  Card as MuiCard,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";

import { Add as AddIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";
import ReusableTable from "../../../components/Common/ReusableTable";
import AvailabilityCards from "./AvailabilityCards";
import AvailabilityForm from "./AvailabilityForm";
import {
  GET_ALL_AVAILABILITIES,
  DELETE_AVAILABILITY_MUTATION,
} from "./graphql/queries";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);

function MyAvailability() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentSpace } = useSpace();
  const [availabilities, setAvailabilities] = useState([]);
  const [selected, setSelected] = useState([]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingAvailability, setEditingAvailability] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    availability: null,
  });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // States for table UI
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewStyle, setViewStyle] = useState("List");
  const [searchQuery, setSearchQuery] = useState("");
  const [availabilityMode, setAvailabilityMode] = useState("my");

  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  // Define table columns
  const columns = useMemo(
    () => [
      {
        field: "serialNumber",
        headerName: "S.No",
        width: 70,
        sortable: false,
        filterable: false,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "timezone",
        headerName: "Timezone",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "daysText",
        headerName: "Days",
        flex: 1.5,
        minWidth: 220,
      },
      {
        field: "details",
        headerName: "Details",
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <Box>
            <Typography variant="body2">
              Time Slots: {params.row.timeSlotsCount}
            </Typography>
            <Typography variant="body2">
              Overrides: {params.row.overridesCount}
            </Typography>
          </Box>
        ),
      },
    ],
    []
  );

  // Prepare data with calculated fields
  const processedAvailabilities = useMemo(() => {
    return availabilities.map((availability) => ({
      ...availability,
      daysText: availability.days.join(", "),
      timeSlotsCount: availability.timeSlots.length,
      overridesCount: availability.overrides.length,
    }));
  }, [availabilities]);

  // Search fields
  const searchFields = ["name", "timezone", "daysText"];

  // Event handlers
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = processedAvailabilities.map((n) => n.id);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialog(true);
  };

  const handleBulkEdit = () => {
    if (selected.length !== 1) {
      setAlert({
        open: true,
        message: "Please select exactly one availability to edit",
        severity: "info",
      });
      return;
    }

    // Find the selected availability
    const selectedAvailability = availabilities.find(
      (av) => av.id === selected[0]
    );
    if (selectedAvailability) {
      setEditingAvailability(selectedAvailability);
      setFormDialogOpen(true);
    }
  };

  const handleEditClick = (availability) => {
    setEditingAvailability(availability);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = (availability) => {
    setDeleteDialog({
      open: true,
      availability: availability,
    });
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      const deletePromises = selected.map((id) =>
        axiosInstance.post("/graphql", {
          query: DELETE_AVAILABILITY_MUTATION,
          variables: { id },
        })
      );

      await Promise.all(deletePromises);
      setAvailabilities((prev) =>
        prev.filter((availability) => !selected.includes(availability.id))
      );
      setSelected([]);
      setAlert({
        open: true,
        message: `Successfully deleted ${selected.length} availabilities`,
        severity: "success",
      });
    } catch (error) {
      setAlert({
        open: true,
        message: `Error deleting availabilities: ${error.message}`,
        severity: "error",
      });
    } finally {
      setBulkDeleteDialog(false);
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleAddNew = () => {
    setEditingAvailability(null);
    setFormDialogOpen(true);
  };

  // Fetch availabilities
  useEffect(() => {
    const fetchAvailabilities = async () => {
      try {
        const response = await axiosInstance.post("/graphql", {
          query: GET_ALL_AVAILABILITIES,
          variables: { spaceId: currentSpaceId },
        });

        if (response.data?.errors) {
          console.error(response.data.errors);
        } else {
          setAvailabilities(response.data?.getAllAvailabilities || []);
        }
      } catch (error) {
        console.error("Error fetching availabilities:", error);
        setAlert({
          open: true,
          message: "Error fetching availabilities",
          severity: "error",
        });
      }
    };

    if (currentSpaceId) {
      fetchAvailabilities();
    }
  }, [currentSpaceId]);

  // Custom actions for toolbar
  const customActions = [
    <Button
      key="add-new"
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={handleAddNew}
      sx={{ mr: 2 }}
    >
      Add New
    </Button>,
  ];

  const queryParamsForBreadcrumb = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const currentSpaceIdForBreadcrumb =
    queryParamsForBreadcrumb.get("spaceId") || currentSpace?.id;

  return (
    <React.Fragment>
      <Helmet title="Availability" />

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Availability
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link
              component={NavLink}
              to={`/spaces/${currentSpaceIdForBreadcrumb}`}
            >
              {currentSpace?.name || "Spaces"}
            </Link>
            <Link
              component={NavLink}
              to={`/calendar/dashboard?spaceId=${currentSpaceIdForBreadcrumb}`}
            >
              Calendar
            </Link>
            <Typography color="text.primary">Availability</Typography>
          </Breadcrumbs>
        </Grid>

        <Grid item>
          <ToggleButtonGroup
            value={availabilityMode}
            exclusive
            onChange={(event, newMode) => {
              if (newMode !== null) {
                setAvailabilityMode(newMode);
              }
            }}
            aria-label="availability mode"
          >
            <ToggleButton value="my" aria-label="my availability">
              My Availability
            </ToggleButton>
            <ToggleButton value="team" aria-label="team availability">
              Team Availability
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      <Divider my={6} />

      {availabilityMode === "my" ? (
        <ReusableTable
          data={processedAvailabilities}
          columns={columns}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search availabilities..."
          searchFields={searchFields}
          selected={selected}
          onSelectAllClick={handleSelectAllClick}
          onSelect={handleClick}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          viewStyle={viewStyle}
          setViewStyle={setViewStyle}
          hasCardView={true}
          CardComponent={AvailabilityCards}
          cardProps={{ xs: 12, sm: 6, md: 4, lg: 3 }}
          onBulkEdit={handleBulkEdit}
          onBulkDelete={handleBulkDelete}
          customActions={customActions}
          title=""
        />
      ) : (
        <TeamAvailability spaceId={currentSpaceId} />
      )}

      {/* Add/Edit Availability Dialog */}
      <Dialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingAvailability ? "Edit Availability" : "Add New Availability"}
        </DialogTitle>
        <AvailabilityForm
          editingAvailability={editingAvailability}
          handleClose={() => setFormDialogOpen(false)}
          onSuccess={(message) => {
            setAlert({
              open: true,
              message: message || "Availability saved successfully",
              severity: "success",
            });
            setFormDialogOpen(false);
            // Refresh data
            if (currentSpaceId) {
              axiosInstance
                .post("/graphql", {
                  query: GET_ALL_AVAILABILITIES,
                  variables: { spaceId: currentSpaceId },
                })
                .then((response) => {
                  setAvailabilities(response.data?.getAllAvailabilities || []);
                });
            }
          }}
        />
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog
        open={bulkDeleteDialog}
        onClose={() => setBulkDeleteDialog(false)}
      >
        <DialogTitle>Confirm Bulk Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} selected
            availabilities? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleBulkDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete {selected.length} Availabilities
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}

export default MyAvailability;
